import type { BlogLocale } from './types';

/** FAQ articolo cohort + prodotti primo acquisto — stesso testo in body markdown e JSON-LD FAQPage. */
export const SHOPIFY_COHORT_ANALYSIS_FAQ_EN = [
  {
    q: 'What is Shopify cohort analysis?',
    a: 'Shopify cohort analysis groups customers by a shared starting point, usually the period of their first purchase, and tracks how those customers behave over time. It is useful for analyzing retention, repeat purchases and customer value.',
  },
  {
    q: 'What is repeat purchase rate by product?',
    a: 'Repeat purchase rate by product measures how many customers who first bought a specific product later placed another order. It helps identify which products create customers who come back.',
  },
  {
    q: 'Why is first-purchase product important?',
    a: 'The first product a customer buys can strongly influence future behavior. Some products attract discount-driven one-time buyers, while others attract customers with higher retention and stronger LTV.',
  },
  {
    q: 'Is high ROAS always good?',
    a: 'No. High first-order ROAS can still produce weak customer economics if repeat purchase rate, margin or LTV are low. A lower-ROAS product can be more profitable if it creates better customers over time.',
  },
  {
    q: 'Should I use gross LTV or net LTV?',
    a: 'Net LTV is usually more useful for decision-making because it accounts for discounts, refunds, COGS and variable costs. Gross LTV can overstate customer value.',
  },
  {
    q: 'How does Kipify help with this analysis?',
    a: 'Kipify connects Shopify, Meta Ads, Google Ads and GA4 so ecommerce teams can analyze store performance, ad spend, custom KPIs, retention, margin and inventory in one place.',
  },
] as const;

export const SHOPIFY_COHORT_ANALYSIS_FAQ_IT = [
  {
    q: "Cos'è l'analisi cohort su Shopify?",
    a: "L'analisi cohort su Shopify raggruppa i clienti in base a un punto di partenza comune, di solito il periodo del primo acquisto, e consente di analizzare come quei clienti si comportano nel tempo.",
  },
  {
    q: 'Cos’è il tasso di riacquisto per prodotto?',
    a: 'È la percentuale di clienti che, dopo aver acquistato uno specifico prodotto nel primo ordine, effettuano almeno un ordine successivo.',
  },
  {
    q: 'Perché il prodotto del primo acquisto è importante?',
    a: 'Perché il primo prodotto può influenzare il comportamento futuro del cliente. Alcuni prodotti attraggono clienti occasionali e molto sensibili allo sconto, altri generano clienti con maggiore retention e LTV.',
  },
  {
    q: 'Un ROAS alto è sempre positivo?',
    a: 'No. Un ROAS alto sul primo ordine può nascondere clienti poco profittevoli se il tasso di riacquisto, il margine o il LTV sono bassi.',
  },
  {
    q: 'Meglio Gross LTV o Net LTV?',
    a: 'Per decisioni operative è meglio il Net LTV, perché considera sconti, resi, costo del prodotto e costi variabili. Il Gross LTV può sovrastimare il valore reale del cliente.',
  },
  {
    q: 'Come può aiutare Kipify?',
    a: 'Kipify collega Shopify, Meta Ads, Google Ads e GA4 per aiutare i team ecommerce ad analizzare performance, advertising, KPI custom, retention, margini e inventory in un’unica vista.',
  },
] as const;

export function getShopifyCohortAnalysisFaqPageJsonLd(locale: BlogLocale): {
  '@type': 'FAQPage';
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: { '@type': 'Answer'; text: string };
  }>;
} {
  const rows =
    locale === 'it'
      ? SHOPIFY_COHORT_ANALYSIS_FAQ_IT
      : SHOPIFY_COHORT_ANALYSIS_FAQ_EN;
  return {
    '@type': 'FAQPage',
    mainEntity: rows.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}
